/**
 * useAIButtonShortcut Hook - SPEC-019 Task 3.3
 *
 * Keyboard shortcut hook for AI Button trigger.
 * - macOS: Cmd+Shift+A
 * - Windows/Linux: Ctrl+Shift+A
 *
 * @module features/ai-button/hooks/useAIButtonShortcut
 * @see SPEC-019 Requirements 1.3, 14.5
 */

import { useEffect, useRef, useCallback } from 'react';

// ============================================================================
// Types
// ============================================================================

export interface UseAIButtonShortcutOptions {
  /** Whether the shortcut is enabled */
  enabled?: boolean;
  /** Callback when shortcut is triggered */
  onTrigger: () => void;
  /** Debounce delay in ms (default: 300) */
  debounceMs?: number;
}

// ============================================================================
// Constants
// ============================================================================

const DEFAULT_DEBOUNCE_MS = 300;

// ============================================================================
// Hook Implementation
// ============================================================================

/**
 * Detect if running on macOS
 */
function isMacOS(): boolean {
  return navigator.platform.toUpperCase().indexOf('MAC') >= 0;
}

/**
 * useAIButtonShortcut Hook
 *
 * Listens for Cmd+Shift+A (macOS) or Ctrl+Shift+A (Windows/Linux)
 * and triggers the callback with debounce protection.
 *
 * @param options - Hook options
 *
 * @example
 * ```tsx
 * useAIButtonShortcut({
 *   onTrigger: () => setIsOpen(true),
 *   enabled: !isLoading,
 * });
 * ```
 */
export function useAIButtonShortcut({
  enabled = true,
  onTrigger,
  debounceMs = DEFAULT_DEBOUNCE_MS,
}: UseAIButtonShortcutOptions): void {
  const lastTriggerRef = useRef<number>(0);

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (!enabled) return;

      const isMac = isMacOS();
      const modifier = isMac ? event.metaKey : event.ctrlKey;

      // Check for Cmd/Ctrl + Shift + A
      if (modifier && event.shiftKey && event.key.toLowerCase() === 'a') {
        event.preventDefault();

        // Debounce check (Property 24)
        const now = Date.now();
        if (now - lastTriggerRef.current < debounceMs) {
          return;
        }
        lastTriggerRef.current = now;

        onTrigger();
      }
    },
    [enabled, onTrigger, debounceMs]
  );

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleKeyDown]);
}
